// ─── VisualizeTab ─────────────────────────────────────────────────────────────
// Data Analytics → Visualize sub-tab. Matches the XD design:
//   - Toolbar: zoom selector, undo/redo, a strip of chart-type picker buttons,
//     collapse chevrons, and a 3-dot menu (View chart / Download CSV / Print).
//   - A real spreadsheet (react-spreadsheet) whose columns are filled
//     dynamically from the parameters checked in the left Site panel:
//     column A = Datetime, B/C/D… = each selected parameter's values,
//     aligned by timestamp and bounded by the D/W/M/Y date range.
//
// Data is fetched + aligned by useVisualizeData; the chart icons open a popup
// that plots the same data (one trace per parameter).
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useMemo, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import Spreadsheet from 'react-spreadsheet'
import { MoreVertical, Loader, Download, Printer, FileText, FileSpreadsheet, ChevronUp, ChevronDown } from 'lucide-react'
import { useVisualizeData } from './useVisualizeData'
import ChartPopup from './ChartPopup'
import useAuthStore from '../../../store/auth-store'
import { resolveIanaTz } from '../../../lib/tz'
import { projectsApi } from '../../../lib/projectsApi'
import { WindRoseEditorModal } from '../DashboardWidgets'

// All chart types shown in a single row — no pagination needed.
// Pie / donut / half-pie / gauge intentionally omitted — they don't fit the
// time-series shape that drives this tab and were never wired to real data.
const ALL_CHARTS = [
    { id: 'line',       label: 'Line Chart',           icon: LineGlyph },
    { id: 'multi-line', label: 'Multi-Chart',          icon: MultiLineGlyph },
    { id: 'combo',      label: 'Combo Chart',          icon: ComboGlyph },
    { id: 'area',       label: 'Area Chart',           icon: AreaGlyph },
    { id: 'column',     label: 'Column Chart',         icon: ColumnGlyph },
    { id: 'column-grp', label: 'Stacked Column Chart', icon: GroupedColumnGlyph },
    { id: 'h-bar',      label: 'Bar Chart',            icon: HBarGlyph },
    { id: 'wind-rose',  label: 'Wind Rose',            icon: WindRoseGlyph },
]

const ZOOM_LEVELS = ['50%', '75%', '100%', '125%', '150%']

// Must match the fixed dimensions in index.css (.dataanalytics-sheet).
const COL_W = 150
const ROW_H = 28
const ROWHDR_W = 44
const MIN_ROWS = 25

function csvEscape(v) {
    const s = v == null ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

export default function VisualizeTab({ projectId: projectIdProp = null, selectedParameters = [], dateRangeGraph, activePeriod, onPeriodChange, externalRefreshNonce = 0, mobileMenuSlot = null }) {
    // useParams() can come back empty depending on where this tab renders, so
    // prefer the projectId threaded down from DashboardPage (reliable) and fall
    // back to the route param. The chart needs it to inherit the Overview Line
    // Chart's Y-Axis combine (see ChartPopup.readOverviewCombine).
    const routeParams = useParams()
    const projectId = projectIdProp ?? routeParams.projectId
    const [zoom, setZoom] = useState('100%')
    const [selectedChartType, setSelectedChartType] = useState('line')
    const [chartOpen, setChartOpen] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    // Mobile only: the chart-type picker pages 4 icons at a time (up/down
    // chevrons), matching the XD. Desktop shows all of them in one row.
    const CHARTS_PER_PAGE = 4
    const MAX_CHART_PAGE = Math.ceil(ALL_CHARTS.length / CHARTS_PER_PAGE) - 1
    const [chartPage, setChartPage] = useState(0)

    // Use the customer's configured timezone so the grid asks for the same
    // slice of time the chart/widget on Overview asks for. Otherwise rows
    // and chart points diverge whenever the browser tz != customer tz.
    const rawCustomerTz = useAuthStore((s) => s.user?.timezone)
    const customerTz    = useMemo(() => resolveIanaTz(rawCustomerTz), [rawCustomerTz])

    const { data, series, tzOffset, loading, error } = useVisualizeData(selectedParameters, dateRangeGraph, activePeriod, externalRefreshNonce, customerTz)

    // Wind Rose has its OWN device/parameter picker (WindRoseEditorModal — the
    // same one the Overview dashboard's Wind Rose widget uses) instead of
    // plotting whatever happens to be checked in the Site panel: a wind rose
    // needs an exact paired wind-speed + wind-direction parameter, which the
    // sidebar's arbitrary multi-select can't guarantee. Clicking the toolbar
    // icon opens this modal; the chart itself only opens after Save.
    const [windRoseModalOpen, setWindRoseModalOpen] = useState(false)
    const [windRoseSettings, setWindRoseSettings] = useState(null) // { widgetName, selectedDevice, windDirectionParam, windSpeedParam, keyColors }
    const [windRoseSeries, setWindRoseSeries] = useState(null)     // { speed: {points}, direction: {points} } once fetched
    const [windRoseLoading, setWindRoseLoading] = useState(false)

    useEffect(() => {
        const speedId = windRoseSettings?.windSpeedParam
        const dirId   = windRoseSettings?.windDirectionParam
        if (!speedId || !dirId) { setWindRoseSeries(null); return }
        let cancelled = false
        setWindRoseLoading(true)
        const opts = { fromDate: dateRangeGraph?.from, toDate: dateRangeGraph?.to, timezone: customerTz, interval: 'raw', agg: 'avg' }
        Promise.all([
            projectsApi.fetchGraphData(Number(speedId), opts),
            projectsApi.fetchGraphData(Number(dirId), opts),
        ]).then(([speedRes, dirRes]) => {
            if (cancelled) return
            setWindRoseSeries({
                speed:     { points: (speedRes?.data || []).filter((p) => p.y !== null && p.y !== undefined) },
                direction: { points: (dirRes?.data   || []).filter((p) => p.y !== null && p.y !== undefined) },
            })
        }).catch(() => { if (!cancelled) setWindRoseSeries(null) })
            .finally(() => { if (!cancelled) setWindRoseLoading(false) })
        return () => { cancelled = true }
    }, [windRoseSettings?.windSpeedParam, windRoseSettings?.windDirectionParam, dateRangeGraph?.from, dateRangeGraph?.to, customerTz])

    const zoomFactor = parseInt(zoom, 10) / 100

    // Measure the grid container so we can pad the matrix to fill it (no blank
    // area on the right) like a real spreadsheet — responsive to resize/zoom.
    const sheetRef = useRef(null)
    const [box, setBox] = useState({ w: 0, h: 0 })
    useEffect(() => {
        const el = sheetRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        const ro = new ResizeObserver((entries) => {
            const cr = entries[0].contentRect
            setBox({ w: cr.width, h: cr.height })
        })
        ro.observe(el)
        return () => ro.disconnect()
    }, [])

    const paddedData = useMemo(() => {
        if (!data.length) return data
        const dataCols = data.reduce((m, r) => Math.max(m, r.length), 0)
        const fitCols = Math.max(0, Math.floor((box.w / zoomFactor - ROWHDR_W) / COL_W))
        const totalCols = Math.max(dataCols, fitCols, 1)
        // Rows pad to the data (or a small minimum) ONLY — we deliberately do
        // NOT fill the viewport height with blank rows anymore. The sheet grows
        // to its content and the page scrolls as one, instead of the grid
        // getting its own inner vertical scrollbar.
        const totalRows = Math.max(data.length, MIN_ROWS)
        const blank = { value: '', readOnly: true }
        const out = []
        for (let r = 0; r < totalRows; r++) {
            const src = data[r]
            const row = new Array(totalCols)
            for (let c = 0; c < totalCols; c++) row[c] = src && src[c] ? src[c] : blank
            out.push(row)
        }
        return out
    }, [data, box.w, zoomFactor])

    const downloadCsv = () => {
        const csv = data.map((row) => row.map((cell) => csvEscape(cell?.value)).join(',')).join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'visualize-data.csv'
        a.click()
        URL.revokeObjectURL(url)
        setMenuOpen(false)
    }

    const downloadXls = async () => {
        const { utils, writeFile } = await import('xlsx')
        const wsData = data.map((row) => row.map((cell) => cell?.value ?? ''))
        const ws = utils.aoa_to_sheet(wsData)
        const wb = utils.book_new()
        utils.book_append_sheet(wb, ws, 'Data')
        writeFile(wb, 'visualize-data.xlsx')
        setMenuOpen(false)
    }

    // Renders the FULL dataset as a real table (jsPDF + jspdf-autotable),
    // not a screenshot of the on-screen grid — the grid only shows whatever
    // is scrolled into view, so a canvas capture of it silently dropped every
    // row/column outside the visible viewport. autoTable paginates on its
    // own and repeats the Site/Device/Parameter header rows on every page,
    // so the export is complete regardless of how much data was selected.
    const downloadPdf = async () => {
        if (!data.length) return
        const { jsPDF } = await import('jspdf')
        const { autoTable } = await import('jspdf-autotable')

        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
        const pageW = doc.internal.pageSize.getWidth()
        const MARGIN = 36

        const now = new Date()
        const fmtDate = (d) => {
            try { return new Intl.DateTimeFormat('en-US', { timeZone: customerTz, year: 'numeric', month: 'long', day: 'numeric' }).format(d) }
            catch { return d.toDateString() }
        }
        const fmtTime = (d) => {
            try { return new Intl.DateTimeFormat('en-US', { timeZone: customerTz, hour: '2-digit', minute: '2-digit', hour12: true }).format(d) }
            catch { return d.toLocaleTimeString() }
        }

        doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(64)
        doc.text(fmtDate(now), MARGIN, MARGIN + 4)
        doc.text(fmtTime(now), MARGIN, MARGIN + 16)
        doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(20)
        doc.text('Data Analytics — Visualize', pageW / 2, MARGIN + 8, { align: 'center' })

        // data = [siteRow, deviceRow, paramRow, ...dataRows] — see useVisualizeData's
        // header-block doc comment. Site/Device/Parameter go in `head` (not `body`)
        // so autoTable repeats them on every page of a multi-page export.
        const [siteRow, deviceRow, paramRow, ...dataRows] = data
        const cellText = (row) => row.map((c) => c?.value ?? '')

        autoTable(doc, {
            startY: MARGIN + 32,
            margin: { left: MARGIN, right: MARGIN },
            styles: { fontSize: 8, cellPadding: 2.5, textColor: 40, overflow: 'linebreak' },
            headStyles: { fillColor: [245, 245, 245], textColor: 30, halign: 'center', fontStyle: 'bold' },
            columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
            theme: 'grid',
            head: [cellText(siteRow), cellText(deviceRow), cellText(paramRow)],
            body: dataRows.map(cellText),
        })

        doc.save('visualize-data.pdf')
        setMenuOpen(false)
    }

    const hasParams = selectedParameters.length > 0

    // A single chart-type button — shared by the desktop (all-in-one-row) and
    // mobile (4-per-page pager) layouts so they can't drift.
    const renderChartButton = ({ id, label, icon: Glyph }) => {
        const active = selectedChartType === id
        return (
            <button
                key={id}
                type="button"
                title={label}
                onClick={() => {
                    setSelectedChartType(id)
                    // Wind Rose configures its device/parameters first (see the
                    // modal rendered below); every other chart type just opens
                    // straight onto the sidebar-selected parameters.
                    if (id === 'wind-rose') setWindRoseModalOpen(true)
                    else setChartOpen(true)
                }}
                className={`cstmvisual shrink-0 w-20 h-12 flex items-center rounded-lg justify-center rounded transition border ${
                    active ? 'bg-[#E8F1F8] border-[#D6D6D6]' : 'bg-white border-[#D6D6D6] hover:bg-gray-50'
                }`}
            >
                <Glyph active={active} />
            </button>
        )
    }

    // The export/print overflow menu (⋮). Rendered in the toolbar on desktop and
    // portalled up into the tab-row group on mobile (to match the XD), both
    // driven by the same `menuOpen` state.
    const overflowMenu = (
        <div className="flex items-center gap-1 relative">
            <button type="button" onClick={() => setMenuOpen((o) => !o)} className="p-1 text-gray-500 hover:bg-gray-100 rounded">
                <MoreVertical className="w-5 h-5" />
            </button>
            {menuOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded shadow-lg z-30 w-56 py-1 text-sm">
                    <button onClick={downloadPdf} disabled={!hasParams} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                        <FileText className="w-4 h-4 text-gray-500 shrink-0" /> Download PDF
                    </button>
                    <button onClick={downloadCsv} disabled={!hasParams} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                        <Download className="w-4 h-4 text-gray-500 shrink-0" /> Download CSV
                    </button>
                    <button onClick={downloadXls} disabled={!hasParams} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                        <FileSpreadsheet className="w-4 h-4 text-gray-500 shrink-0" /> Download XLS
                    </button>
                    <button onClick={() => { window.print(); setMenuOpen(false) }} disabled={!hasParams} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                        <Printer className="w-4 h-4 text-gray-500 shrink-0" /> Print
                    </button>
                </div>
            )}
        </div>
    )

    return (
        <div className="flex flex-col gap-3 flex-1 px-2 md:px-4 pb-4 min-h-0" style={{ fontFamily: "'Open Sans', sans-serif" }}>
            {/* Toolbar — XD: #F1F2F4 background, 8px radius */}
            <div className="flex items-center gap-3 bg-[#F1F2F4] rounded-lg px-1 md:px-3 py-1">
                {/* Left: zoom — desktop only (the XD mobile toolbar has no zoom). */}
                <div className="hidden md:flex items-center gap-2">
                    <select
                        value={zoom}
                        onChange={(e) => setZoom(e.target.value)}
                        className="bg-gray-50 border border-gray-200 px-2 py-1 rounded text-xs text-gray-700 hover:bg-gray-100 focus:outline-none"
                    >
                        {ZOOM_LEVELS.map((z) => <option key={z} value={z}>{z}</option>)}
                    </select>
                </div>

                {/* Desktop: every chart-type button in one centered row. */}
                <div className="hidden md:flex flex-1 items-center justify-center gap-1.5">
                    {ALL_CHARTS.map(renderChartButton)}
                </div>

                {/* Mobile: show CHARTS_PER_PAGE icons and page through the rest
                    with the up/down chevrons on the right (matches the XD). */}
                <div className="flex md:hidden flex-1 min-w-0 items-center gap-1.5">
                    <div className="flex-1 min-w-0 flex items-center gap-1.5 overflow-hidden">
                        {ALL_CHARTS
                            .slice(chartPage * CHARTS_PER_PAGE, chartPage * CHARTS_PER_PAGE + CHARTS_PER_PAGE)
                            .map(renderChartButton)}
                    </div>
                    <div className="shrink-0 flex flex-col border border-[#D6D6D6] rounded-lg overflow-hidden bg-white">
                        <button
                            type="button"
                            aria-label="Previous chart types"
                            onClick={() => setChartPage((p) => Math.max(0, p - 1))}
                            disabled={chartPage === 0}
                            className="px-1.5 py-0.5 text-gray-600 hover:bg-gray-100 disabled:opacity-30"
                        >
                            <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            aria-label="More chart types"
                            onClick={() => setChartPage((p) => Math.min(MAX_CHART_PAGE, p + 1))}
                            disabled={chartPage === MAX_CHART_PAGE}
                            className="px-1.5 py-0.5 text-gray-600 hover:bg-gray-100 disabled:opacity-30 border-t border-[#D6D6D6]"
                        >
                            <ChevronDown className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Right: overflow menu — desktop in the toolbar; on mobile it is
                    portalled up into the tab-row group (see mobileMenuSlot). */}
                <div className="hidden md:flex">
                    {overflowMenu}
                </div>
            </div>

            {/* Mobile: render the ⋮ menu inside the tab-row group next to the
                calendar, so the toolbar row is just charts + pager (matches XD). */}
            {mobileMenuSlot && createPortal(<div className="md:hidden">{overflowMenu}</div>, mobileMenuSlot)}

            {error && (
                <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">{error}</div>
            )}

            {/* Spreadsheet — natural height (grows to its rows) with only a
                HORIZONTAL scrollbar for wide tables. No vertical overflow here,
                so the grid never shows an inner vertical scrollbar; the page
                (main content area) provides the single vertical scroll. */}
            <div ref={sheetRef} className="da-mobile-scroll cstm-tbl-v bg-white border border-gray-200 overflow-auto max-h-[65vh] md:max-h-none md:flex-1 md:min-h-0">
                {!hasParams ? (
                    <div className="min-h-[300px] flex items-center justify-center text-sm text-gray-400 p-8 text-center">
                        Select one or more parameters from the Site panel to fill the sheet.
                    </div>
                ) : loading ? (
                    <div className="min-h-[300px] flex items-center justify-center gap-2 text-sm text-gray-500">
                        <Loader className="w-4 h-4 animate-spin" /> Loading data…
                    </div>
                ) : (
                    <div style={{ zoom: zoomFactor }} className="dataanalytics-sheet">
                        <Spreadsheet data={paddedData} onChange={() => {}} />
                    </div>
                )}
            </div>

            <ChartPopup
                open={chartOpen}
                onClose={() => setChartOpen(false)}
                chartType={selectedChartType}
                series={series}
                loading={loading}
                tzOffset={tzOffset}
                customerTz={customerTz}
                activePeriod={activePeriod}
                onPeriodChange={onPeriodChange}
                windRoseSeries={windRoseSeries}
                windRoseLoading={windRoseLoading}
                windRoseWidgetName={windRoseSettings?.widgetName}
                windRoseKeyColors={windRoseSettings?.keyColors}
                onEditWindRose={() => setWindRoseModalOpen(true)}
            />

            {/* Wind Rose device/parameter picker — same modal the Overview dashboard's
                Wind Rose widget uses. Opens on toolbar click (before the chart) and
                again from the popup's kebab menu → Chart Settings (via onEditWindRose). */}
            <WindRoseEditorModal
                open={windRoseModalOpen}
                projectId={projectId}
                initialSettings={windRoseSettings || {}}
                onClose={() => setWindRoseModalOpen(false)}
                onSave={(settings) => {
                    setWindRoseSettings(settings)
                    setWindRoseModalOpen(false)
                    setSelectedChartType('wind-rose')
                    setChartOpen(true)
                }}
            />
        </div>
    )
}

// ──────────────────────────────────────────────────────────────────────────────
// Chart-type glyphs — small SVGs to keep visual fidelity without another icon set.
// ──────────────────────────────────────────────────────────────────────────────

// XD palette — icons are two-tone blue + green (gauges use R/Y/G), matching the
// Adobe XD chart-toolbar glyphs. Active state is shown by the button border, so
// glyph colours are fixed.
const BLUE = '#358ECD'
const GREEN = '#5EA64D'
const G_RED = '#E0563A'
const G_YEL = '#E8B23A'
const ORANGE = '#E0A030'

function LineGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20" fill="none">
            <path d="M2 14 L7 9 L12 12 L18 4 L22 8" stroke={BLUE} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}
function MultiLineGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20" fill="none">
            <path d="M2 13 L7 8 L12 11 L18 4 L22 7" stroke={BLUE} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M2 17 L7 14 L12 16 L18 11 L22 14" stroke={GREEN} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}
// Combo — green columns with a blue line trace overlaid (bar + line combo).
function ComboGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <rect x="3"  y="11" width="3.2" height="7"  fill={GREEN} fillOpacity="0.55" />
            <rect x="9"  y="8"  width="3.2" height="10" fill={GREEN} fillOpacity="0.55" />
            <rect x="15" y="12" width="3.2" height="6"  fill={GREEN} fillOpacity="0.55" />
            <path d="M2 13 L10.5 6 L16.5 10 L22 5" stroke={BLUE} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}
// Area — green fill under a blue line.
function AreaGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <path d="M2 16 L7 9 L12 12 L18 4 L22 8 L22 18 L2 18 Z" fill={GREEN} fillOpacity="0.45" />
            <path d="M2 16 L7 9 L12 12 L18 4 L22 8" fill="none" stroke={BLUE} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}
// Single-series columns (XD green columns).
function ColumnGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <rect x="3"  y="10" width="3.2" height="8"  fill={GREEN} />
            <rect x="8"  y="6"  width="3.2" height="12" fill={GREEN} />
            <rect x="13" y="11" width="3.2" height="7"  fill={GREEN} />
            <rect x="18" y="4"  width="3.2" height="14" fill={GREEN} />
        </svg>
    )
}
// Grouped columns — blue + green (XD).
function GroupedColumnGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <rect x="2"  y="9"  width="2.6" height="9"  fill={BLUE} />
            <rect x="5"  y="6"  width="2.6" height="12" fill={GREEN} />
            <rect x="10" y="11" width="2.6" height="7"  fill={BLUE} />
            <rect x="13" y="8"  width="2.6" height="10" fill={GREEN} />
            <rect x="18" y="5"  width="2.6" height="13" fill={BLUE} />
            <rect x="21" y="3"  width="2.6" height="15" fill={GREEN} />
        </svg>
    )
}
// Horizontal bars (XD green).
function HBarGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <rect x="2" y="3"  width="13" height="3" rx="1" fill={GREEN} />
            <rect x="2" y="8.5" width="20" height="3" rx="1" fill={GREEN} />
            <rect x="2" y="14" width="9"  height="3" rx="1" fill={GREEN} />
        </svg>
    )
}
// Two-tone pie (blue + green).
function PieGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <circle cx="12" cy="10" r="8" fill={GREEN} />
            <path d="M12 10 L12 2 A8 8 0 0 1 19.4 12.6 Z" fill={BLUE} />
        </svg>
    )
}
// Two-tone donut (blue + green ring).
function DonutGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <circle cx="12" cy="10" r="6.5" fill="none" stroke={GREEN} strokeWidth="4" />
            <path d="M12 3.5 A6.5 6.5 0 0 1 18.5 10" fill="none" stroke={BLUE} strokeWidth="4" />
        </svg>
    )
}
// Pie variant — blue + green with an exploded slice.
function HalfPieGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <circle cx="11" cy="10" r="7.5" fill={BLUE} />
            <path d="M11 10 L11 2.5 A7.5 7.5 0 0 1 17.5 6.5 Z" fill={GREEN} transform="translate(1.2,-0.8)" />
        </svg>
    )
}
// Wind Rose — compass ring with four coloured petal wedges radiating from a
// centre dot, echoing the polar sector layout of the real chart.
function WindRoseGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <circle cx="12" cy="10" r="8.5" fill="none" stroke="#CBD5E1" strokeWidth="1" />
            <path d="M12 10 L12 2.5 L15 10 Z" fill={BLUE} />
            <path d="M12 10 L19 7 L12 12.5 Z" fill={GREEN} />
            <path d="M12 10 L12 17.5 L9.5 10 Z" fill={ORANGE} />
            <path d="M12 10 L5 12.5 L11 9 Z" fill={G_RED} />
            <circle cx="12" cy="10" r="1.6" fill="#4B5563" />
        </svg>
    )
}
// Speedometer gauge — green / yellow / red arc with a needle.
function GaugeGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <path d="M4 16 A8 8 0 0 1 8 8.1" fill="none" stroke={GREEN} strokeWidth="2.4" strokeLinecap="round" />
            <path d="M8 8.1 A8 8 0 0 1 16 8.1" fill="none" stroke={G_YEL} strokeWidth="2.4" />
            <path d="M16 8.1 A8 8 0 0 1 20 16" fill="none" stroke={G_RED} strokeWidth="2.4" strokeLinecap="round" />
            <line x1="12" y1="16" x2="15.5" y2="9.5" stroke="#444" strokeWidth="1.4" strokeLinecap="round" />
            <circle cx="12" cy="16" r="1.4" fill="#444" />
        </svg>
    )
}
